import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMountedState } from 'react-use';
import { v4 } from 'uuid';
import { USER_PREFERENCE_SETS } from 'common/utils/constants';
import { logger } from 'common/utils/helpers';
import { selectUserPreferences } from 'redux/selectors';
import { addToastNotification } from 'redux/actions';
import { deleteUserPreference, patchUserPreference, selectDefaultUserPreference } from 'redux/UserPreferences';
import Modal from 'components/shared/Modal/Modal';
import { ProgressIndicator } from 'components/shared/Progress';
import ToastNotificationActionTypes from 'components/shared/ToastNotifications/constants';
import ChartSetPreferenceImport from '../ChartSetPreferenceImport/ChartSetPreferenceImport';
import ChartSetPreferenceList from './ChartSetPreferenceList';
import styles from './ChartSetPreferenceListModal.module.css';

function ChartSetPreferenceListModal({ onClose }) {
  const dispatch = useDispatch();
  const isMounted = useMountedState();
  const defaultPreference = useSelector((state) =>
    selectDefaultUserPreference(selectUserPreferences(state), USER_PREFERENCE_SETS.CHART_SET)
  );
  const [submitting, setSubmitting] = useState(false);
  const updatePreferenceName = useCallback(
    (preference) => {
      setSubmitting(true);
      const trimmedPreferenceName = preference.name.trim();

      return dispatch(
        patchUserPreference({
          preferenceId: preference.preferenceId,
          category: preference.category,
          name: trimmedPreferenceName
        })
      )
        .then(() => {
          dispatch(
            addToastNotification({
              id: v4(),
              title: trimmedPreferenceName,
              description: 'changed its name successfully.'
            })
          );
        })
        .catch((error) => {
          logger.error(error);

          dispatch(
            addToastNotification({
              id: v4(),
              title: trimmedPreferenceName,
              description: 'failed to change its name. Try again'
            })
          );
        })
        .finally(() => {
          if (isMounted()) {
            setSubmitting(false);
          }
        });
    },
    [dispatch, isMounted]
  );
  const togglePreferenceDefault = useCallback(
    (preference) => {
      setSubmitting(true);

      return dispatch(
        patchUserPreference({
          preferenceId: preference.preferenceId,
          category: preference.category,
          default: !preference.default
        })
      )
        .then(() => {
          dispatch(
            addToastNotification({
              id: v4(),
              title: preference.name,
              description: !preference.default
                ? 'is set as the new default chart set.'
                : 'is unset as the default chart set.'
            })
          );
        })
        .catch((error) => {
          logger.error(error);

          dispatch(
            addToastNotification({
              id: v4(),
              title: preference.name,
              description: `Failed to set default for`,
              reverse: true
            })
          );
        })
        .finally(() => {
          if (isMounted()) {
            setSubmitting(false);
          }
        });
    },
    [dispatch, isMounted]
  );
  const toggleLocalPreferenceDefault = useCallback(
    (preference) => {
      if (preference.local) {
        if (preference.preferenceId !== defaultPreference.preferenceId) {
          return togglePreferenceDefault(defaultPreference);
        }

        return Promise.resolve();
      }

      return togglePreferenceDefault(preference);
    },
    [togglePreferenceDefault, defaultPreference]
  );
  const deletePreference = useCallback(
    (preference) => {
      if (preference.local) {
        return Promise.resolve();
      }

      setSubmitting(true);

      return dispatch(deleteUserPreference(preference))
        .then(() => {
          dispatch(
            addToastNotification({
              id: v4(),
              title: preference.name,
              description: 'was removed.',
              actionMessage: 'Undo',
              actionType: ToastNotificationActionTypes.UNDO_REMOVED_CHART_SET,
              preferenceId: preference.preferenceId,
              preferenceCategory: USER_PREFERENCE_SETS.CHART_SET,
              groupName: preference.name,
              callback: (isActionPending) => {
                if (isMounted()) {
                  setSubmitting(isActionPending);
                }
              }
            })
          );
        })
        .catch((error) => {
          logger.error(error);

          dispatch(
            addToastNotification({
              id: v4(),
              title: preference.name,
              description: ' failed to be removed'
            })
          );
        })
        .finally(() => {
          if (isMounted()) {
            setSubmitting(false);
          }
        });
    },
    [dispatch, isMounted]
  );
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal animateX={false} global close={handleClose} noPadding height={800} width={1024}>
      <div className={styles.container}>
        {submitting && <ProgressIndicator />}
        <div className={styles.header_container}>
          <div className={styles.header}>Manage chart sets</div>
          <ChartSetPreferenceImport />
        </div>
        <div className={styles.body}>
          <ChartSetPreferenceList
            defaultPreferenceId={defaultPreference?.preferenceId}
            onUpdateName={updatePreferenceName}
            onToggleDefault={toggleLocalPreferenceDefault}
            onDelete={deletePreference}
          />
        </div>
      </div>
    </Modal>
  );
}

export default ChartSetPreferenceListModal;
